/*
 * DESIGNER INSTRUCTIONS FOR PRODUCT IMAGES:
 *
 * Each product needs the following visual assets:
 *
 * 1. PRODUCT IMAGE (used in ProductScreen.jsx)
 *    - High-quality product photography
 *    - Size: 400x400px minimum, square aspect ratio
 *    - Format: PNG with transparency preferred
 *    - Show: Vial/syringe in professional medical setting
 *    - Style: Clean, modern, professional medical aesthetic
 *
 * 2. INGREDIENT ICONS (used in FormulationScreen.jsx)
 *    - Simple, modern icons for each ingredient
 *    - Size: 80x80px, SVG format preferred
 *    - Style: Line icons or minimal solid icons
 *    - Color: Charcoal (#2D2D2D) or Onyx Green (#3D5A4C)
 *
 * 3. COMPARISON CHART GRAPHICS (used in SpeedScreen.jsx)
 *    - Visual comparison of treatment speed
 *    - Infographic style showing time/effectiveness
 *    - Size: Flexible, responsive design
 *    - Include: Clocks, arrows, progress bars
 *
 * 4. BENEFIT ICONS (used in DetailsScreen.jsx)
 *    - Icon for each benefit listed
 *    - Size: 48x48px, SVG format
 *    - Style: Consistent with brand
 *    - Examples: Energy bolt, shield, heart, brain
 */

// Med ID configuration by environment
const medIds = {
  development: {
    // Use staging med IDs for local development
    nad: 'C-Xn4Zb8K0VmLpQ7EaGdw1YcU2HTjoR9Ms',
    glutathione: 'C-Rt8Yw5NcXz0LpJ3oM7qBvH1dE2UfKaG',
    vitaminb12: 'C-Jr7Vq0Gx3Lz8Tc5Fp1Nb9Yw2Km6HdSo',
    lcarnitine: 'C-Xd5Jm2Vq9Ky1Zt3Lo6Rg4Hp0Nw8FsUa'
  },
  staging: {
    nad: 'C-Xn4Zb8K0VmLpQ7EaGdw1YcU2HTjoR9Ms',
    glutathione: 'C-Rt8Yw5NcXz0LpJ3oM7qBvH1dE2UfKaG',
    vitaminb12: 'C-Jr7Vq0Gx3Lz8Tc5Fp1Nb9Yw2Km6HdSo',
    lcarnitine: 'C-Xd5Jm2Vq9Ky1Zt3Lo6Rg4Hp0Nw8FsUa'
  },
  production: {
    nad: 'C-2812c9108784ee5a7d76da3c95bd7186',
    glutathione: 'C-63fccd6f95219ab2579f6c192e5cad8f',
    vitaminb12: 'C-d7d6e71ac959c532f72e4e9f3dbaadb0',
    lcarnitine: 'C-1c2d242558a2edce5d63ae2929bdb175'
  }
};

// Determine current environment and select appropriate med IDs
const currentEnv = import.meta.env.VITE_ENV || 'production';
const envMedIds = medIds[currentEnv];

export const products = {
  nad: {
    id: 'nad',
    medId: envMedIds.nad,
    name: 'NAD+ Injection',
    tagline: 'Cellular Energy & Longevity',
    couponCode: 'SAVE70',
    description: 'NAD+ supplementation supports cellular energy production, DNA repair, and healthy aging.',
    price: 299,
    priceInterval: 'month',
    supply: '12-Month Supply (Delivered Monthly)',
    isDaily: false,
    // DESIGNER: Product image showing NAD+ vial with medical aesthetic
    productImage: '/images/products/nad-vial.png',
    ingredients: [
      {
        name: 'NAD+ (Nicotinamide Adenine Dinucleotide)',
        amount: '200mg/ml',
        purpose: 'Supports cellular energy production and metabolism',
        // DESIGNER: Icon showing molecule/cellular structure
        icon: '/images/icons/molecule.svg'
      }
    ],
    benefits: [
      'Enhanced cellular energy',
      'Improved cognitive function',
      'DNA repair support',
      'Healthy aging support'
    ],
    features: [
      'Monthly injections',
      'Prescription strength',
      'Medical provider oversight',
      'Delivered to your door'
    ]
  },

  glutathione: {
    id: 'glutathione',
    medId: envMedIds.glutathione,
    name: 'Glutathione Injection',
    tagline: 'Master Antioxidant',
    couponCode: 'SAVE60',
    description: 'Glutathione is the body\'s most powerful antioxidant, supporting detoxification, immune function, and skin health.',
    price: 249,
    priceInterval: 'month',
    supply: '12-Month Supply (Delivered Monthly)',
    isDaily: false,
    // DESIGNER: Product image showing Glutathione vial, lighter/brighter aesthetic
    productImage: '/images/products/glutathione-vial.png',
    ingredients: [
      {
        name: 'Glutathione',
        amount: '200mg/ml',
        purpose: 'Master antioxidant supporting detoxification and immune function',
        // DESIGNER: Icon showing shield or protective symbol
        icon: '/images/icons/shield.svg'
      }
    ],
    benefits: [
      'Powerful antioxidant protection',
      'Supports detoxification',
      'Brightens skin tone',
      'Immune system support'
    ],
    features: [
      'Monthly injections',
      'Prescription strength',
      'Medical provider oversight',
      'Delivered to your door'
    ]
  },

  vitaminb12: {
    id: 'vitaminb12',
    medId: envMedIds.vitaminb12,
    name: 'Vitamin B12 Injection',
    tagline: 'Energy & Nerve Health',
    couponCode: 'SAVE30',
    description: 'Vitamin B12 injections provide superior absorption for enhanced energy, mental clarity, and nervous system support.',
    price: 149,
    priceInterval: 'month',
    supply: '12-Month Supply (Delivered Monthly)',
    isDaily: false,
    // DESIGNER: Product image showing B12 vial with vibrant red/pink tint
    productImage: '/images/products/b12-vial.png',
    ingredients: [
      {
        name: 'Methylcobalamin (Vitamin B12)',
        amount: '1000mcg/ml',
        purpose: 'Supports energy production, red blood cell formation, and nervous system health',
        // DESIGNER: Icon showing lightning bolt or energy symbol
        icon: '/images/icons/energy-bolt.svg'
      }
    ],
    benefits: [
      'Increased energy levels',
      'Enhanced mental clarity',
      'Supports nervous system',
      'Improves red blood cell formation'
    ],
    features: [
      'Monthly injections',
      'Prescription strength',
      'Medical provider oversight',
      'Delivered to your door'
    ]
  },

  lcarnitine: {
    id: 'lcarnitine',
    medId: envMedIds.lcarnitine,
    name: 'L-Carnitine Injection',
    tagline: 'Metabolism & Fat Burning',
    couponCode: 'SAVE50',
    description: 'L-Carnitine supports fat metabolism by transporting fatty acids into cells for energy production, enhancing athletic performance and weight management.',
    price: 199,
    priceInterval: 'month',
    supply: '12-Month Supply (Delivered Monthly)',
    isDaily: false,
    // DESIGNER: Product image showing L-Carnitine vial with dynamic/athletic aesthetic
    productImage: '/images/products/lcarnitine-vial.png',
    ingredients: [
      {
        name: 'L-Carnitine',
        amount: '500mg/ml',
        purpose: 'Facilitates fat metabolism and energy production',
        // DESIGNER: Icon showing flame or metabolic process
        icon: '/images/icons/flame.svg'
      }
    ],
    benefits: [
      'Enhanced fat metabolism',
      'Increased athletic performance',
      'Improved energy production',
      'Supports weight management'
    ],
    features: [
      'Monthly injections',
      'Prescription strength',
      'Medical provider oversight',
      'Delivered to your door'
    ]
  }
};

// Get product from URL parameter
export function getProductFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  const productParam = urlParams.get('product')?.toLowerCase();
  return productParam || 'nad'; // Default to NAD+
}

// Get product configuration by ID
export function getProductByParam(productId) {
  return products[productId?.toLowerCase()] || products.nad;
}

// Get product by medId
export function getProductByMedId(medId) {
  return Object.values(products).find(p => p.medId === medId) || products.nad;
}
